// src/pages/Events.jsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ReviewsBar from "../components/ReviewsBar";
import api from "../utils/api";
import "../global.css";
import "../styles/faqPage.css";  // re-using same clean grid layout

const Events = () => {
  const [events, setEvents] = useState({
    music: [],
    sports: [],
    theatre: [],
    comedy: [],
    family: [],
    lastUpdated: null,
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/api/open/events/london")
      .then((res) => {
        setEvents(res.data || {});
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const categories = [
    { key: "music", label: "🎵 Upcoming Music Events" },
    { key: "sports", label: "⚽ Sports & Stadium Events" },
    { key: "theatre", label: "🎭 Theatre & Live Shows" },
    { key: "comedy", label: "😂 Comedy & Stand-Up" },
    { key: "family", label: "👨‍👩‍👧 Family & Festival Events" },
  ];

  return (
    <div className="faq-page">
      {/* ⭐ SEO META */}
      <Helmet>
        <title>London & Heathrow Events Guide | Concerts, Sports & Shows | CheapCabGrab</title>
        <meta
          name="description"
          content="See the top upcoming events in London — concerts, football, theatre, comedy and more. Updated regularly. Book taxis, minibuses or airport transfers for event travel."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/events" />
        <meta
          name="keywords"
          content="London events, concerts London, sports events London, O2 events, Wembley events, Heathrow events, event taxi, event minibus"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="London & Heathrow Events Guide | Updated Weekly" />
        <meta
          property="og:description"
          content="Discover London’s biggest concerts, football matches, theatre shows, comedy nights and family events. Updated automatically every week."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/events" />

        {/* JSON-LD for Events Page */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "London Events Guide",
            description:
              "Upcoming concerts, stadium events, theatre shows, comedy and family festivals in London. Automatically updated.",
          })}
        </script>
      </Helmet>

      {/* ⭐ TWO-COLUMN GRID */}
      <div className="faq-grid">
        {/* LEFT SECTION: EVENTS */}
        <div className="faq-left">
          <header className="faq-header">
            <h1>🎉 London Events – What’s On</h1>
            <p>
              Live, automatically updated list of the best concerts, football matches, theatre,
              comedy shows and family events across London.  
              <br />
              <small>Last updated: {events.lastUpdated || "…"}</small>
            </p>
          </header>

          {/* LOADING */}
          {loading && <p>Loading events…</p>}

          {/* CATEGORIES */}
          {!loading &&
            categories.map((cat) => {
              const list = (events[cat.key] || [])
                .filter((ev) => new Date(ev.date) >= new Date())
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .slice(0, 5);

              if (!list.length) return null;

              return (
                <section key={cat.key} className="faq-section">
                  <h2>{cat.label}</h2>

                  <div className="events-clean-grid">
                    {list.map((ev, idx) => (
                      <article className="event-clean-card" key={idx}>
                        <h3>{ev.name}</h3>
                        <p>📅 {ev.date}</p>
                        <p>📍 {ev.venue}</p>

                        <div className="event-card-actions">
                          <a
                            href={ev.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="event-ticket-btn"
                          >
                            🎟️ Buy Tickets
                          </a>

                          <Link className="event-taxi-btn" to="/">
                            🚖 Book Taxi
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              );
            })}

          {/* EXTRA INTERNAL SEO LINKS */}
          <section className="faq-section" style={{ marginTop: "30px" }}>
            <h2>🚖 Travelling to an Event?</h2>
            <p>Book reliable transfers to any London venue:</p>
            <ul>
              <li><Link className="faq-link" to="/heathrow-taxi">Heathrow Taxi Transfers</Link></li>
              <li><Link className="faq-link" to="/minibus-hire">7–14 Seater Minibus Hire</Link></li>
              <li><Link className="faq-link" to="/coach">Large Coach Hire (25+ seats)</Link></li>
              <li><Link className="faq-link" to="/slough-taxi">Slough Taxis</Link></li>
              <li><Link className="faq-link" to="/windsor-taxi">Windsor Taxis</Link></li>
              <li><Link className="faq-link" to="/maidenhead-taxi">Maidenhead Taxis</Link></li>
            </ul>
          </section>

          <div style={{ marginTop: "40px" }}>
            <Link to="/" className="event-footer-cta">
              🚖 Get an Instant Taxi Quote
            </Link>
          </div>
        </div>

        {/* RIGHT COLUMN – REVIEWS */}
        <aside className="faq-right">
          <div className="faq-reviews-card">
            <h2>⭐ Customer Reviews</h2>
            <ReviewsBar />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Events;

